import React, { useState } from 'react';
import { Table, Form, Button, Row, Col } from 'react-bootstrap';
import { FaTrash, FaEdit } from 'react-icons/fa';

const EstimateSchemesForm = ({ 
  setSchemeSalesData, 
  selectedMobile 
}) => {
  const [schemeTableData, setSchemeTableData] = useState([]);
  const [schemeFormData, setSchemeFormData] = useState({
    scheme_name: '',
    scheme_type: '',
    discount_percentage: '',
    minimum_amount: '',
    maximum_discount: '',
    valid_from: '',
    valid_to: '',
    description: ''
  });

  const [isEditing, setIsEditing] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSchemeFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAdd = () => {
    if (isEditing) {
      // Update existing item
      const updatedData = [...schemeTableData];
      updatedData[editingIndex] = {
        ...schemeFormData,
        id: Date.now()
      };
      setSchemeTableData(updatedData);
      setSchemeSalesData(updatedData);
      setIsEditing(false);
      setEditingIndex(null);
    } else {
      // Add new item
      const newItem = {
        ...schemeFormData,
        id: Date.now()
      };
      const updatedData = [...schemeTableData, newItem];
      setSchemeTableData(updatedData);
      setSchemeSalesData(updatedData);
    }
    
    // Reset form
    setSchemeFormData({
      scheme_name: '',
      scheme_type: '',
      discount_percentage: '',
      minimum_amount: '',
      maximum_discount: '',
      valid_from: '',
      valid_to: '',
      description: ''
    });
  };

  const handleEdit = (index) => {
    const item = schemeTableData[index];
    setSchemeFormData(item);
    setIsEditing(true);
    setEditingIndex(index);
  };

  const handleDelete = (index) => {
    const updatedData = schemeTableData.filter((_, i) => i !== index);
    setSchemeTableData(updatedData);
    setSchemeSalesData(updatedData);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditingIndex(null);
    setSchemeFormData({
      scheme_name: '',
      scheme_type: '',
      discount_percentage: '',
      minimum_amount: '',
      maximum_discount: '',
      valid_from: '',
      valid_to: '',
      description: ''
    });
  };

  return (
    <div className="estimate-schemes-form">
      <h5>Schemes Form</h5>
      
      {/* Form */}
      <Row className="mb-3">
        <Col md={3}>
          <Form.Group>
            <Form.Label>Scheme Name</Form.Label>
            <Form.Control
              type="text"
              name="scheme_name"
              value={schemeFormData.scheme_name}
              onChange={handleInputChange}
              placeholder="Scheme Name"
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Scheme Type</Form.Label>
            <Form.Select
              name="scheme_type"
              value={schemeFormData.scheme_type}
              onChange={handleInputChange}
            >
              <option value="">Select Type</option>
              <option value="percentage">Percentage Discount</option>
              <option value="fixed">Fixed Amount</option>
              <option value="buy_one_get_one">Buy One Get One</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Discount Percentage</Form.Label>
            <Form.Control
              type="number"
              name="discount_percentage"
              value={schemeFormData.discount_percentage}
              onChange={handleInputChange}
              placeholder="Discount %"
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Minimum Amount</Form.Label>
            <Form.Control
              type="number"
              name="minimum_amount"
              value={schemeFormData.minimum_amount}
              onChange={handleInputChange}
              placeholder="Min Amount"
            />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={3}>
          <Form.Group>
            <Form.Label>Maximum Discount</Form.Label>
            <Form.Control
              type="number"
              name="maximum_discount"
              value={schemeFormData.maximum_discount}
              onChange={handleInputChange}
              placeholder="Max Discount"
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Valid From</Form.Label>
            <Form.Control
              type="date"
              name="valid_from"
              value={schemeFormData.valid_from}
              onChange={handleInputChange}
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Valid To</Form.Label>
            <Form.Control
              type="date"
              name="valid_to"
              value={schemeFormData.valid_to}
              onChange={handleInputChange}
            />
          </Form.Group>
        </Col>
        <Col md={3} className="d-flex align-items-end">
          <div>
            <Button 
              variant="primary" 
              onClick={handleAdd}
              className="me-2"
              size="sm"
            >
              {isEditing ? 'Update' : 'Add'}
            </Button>
            {isEditing && (
              <Button 
                variant="secondary" 
                onClick={handleCancel}
                size="sm"
              >
                Cancel
              </Button>
            )}
          </div>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={9}>
          <Form.Group>
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              name="description"
              value={schemeFormData.description}
              onChange={handleInputChange}
              placeholder="Scheme Description"
            />
          </Form.Group>
        </Col>
      </Row>

      {/* Table */}
      <div className="table-responsive">
        <Table bordered hover size="sm">
          <thead>
            <tr>
              <th>SI</th>
              <th>Scheme Name</th>
              <th>Scheme Type</th>
              <th>Discount %</th>
              <th>Min Amount</th>
              <th>Max Discount</th>
              <th>Valid From</th>
              <th>Valid To</th>
              <th>Description</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {schemeTableData.map((item, index) => (
              <tr key={item.id || index}>
                <td>{index + 1}</td>
                <td>{item.scheme_name}</td>
                <td>{item.scheme_type}</td>
                <td>{item.discount_percentage}%</td>
                <td>₹{item.minimum_amount}</td>
                <td>₹{item.maximum_discount}</td>
                <td>{item.valid_from}</td>
                <td>{item.valid_to}</td>
                <td>{item.description}</td>
                <td>
                  <FaEdit
                    style={{ cursor: 'pointer', color: 'blue', marginRight: '10px' }}
                    onClick={() => handleEdit(index)}
                  />
                  <FaTrash
                    style={{ cursor: 'pointer', color: 'red' }}
                    onClick={() => handleDelete(index)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

export default EstimateSchemesForm; 